import { useEffect, useState } from "react";
import type { Urgency as UrgencyType } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation, useRoute } from "wouter";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCreateLead, useUpdateLead, useGetLead, useListSpaces, getListLeadsQueryKey, getGetLeadQueryKey, getGetLeadsSummaryQueryKey, LeadSource, SpaceType, Urgency, LeadStage, CallStatus, type Lead, type Space } from "@workspace/api-client-react";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";

const objectionTypeValues = ["", "location", "price", "other"] as const;

const URGENCY_LABELS: Record<UrgencyType, string> = {
  immediate: "Immediately",
  high: "This Week",
  medium: "This Month",
  low: "Exploring",
};

const TIME_SLOTS = [
  "10:00-12:00",
  "12:00-14:00",
  "14:00-16:00",
  "16:00-18:00",
  "18:00-20:00",
] as const;

const FOLLOW_UP_CALL_STATUSES = ["follow_up_1", "follow_up_2", "follow_up_3"] as const;
type FollowUpStatus = (typeof FOLLOW_UP_CALL_STATUSES)[number];
const isFollowUpStatus = (s: string): s is FollowUpStatus =>
  (FOLLOW_UP_CALL_STATUSES as readonly string[]).includes(s);

const NATURE_OF_BUSINESS_OPTIONS = [
  "Startup / Tech",
  "Freelancer / Consultant",
  "Marketing / Agency",
  "Design / Creative Studio",
  "SaaS / Software",
  "E-commerce",
  "Media / Content / Podcast",
  "Education / Coaching",
  "Accounting / Finance",
  "Legal / Compliance",
  "Healthcare / Wellness",
  "Real Estate",
  "Trading / Distribution",
  "Recruitment / HR",
  "Non-Profit / NGO",
] as const;

const todayIst = (): string =>
  new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });

const formSchema = z.object({
  customerName: z.string().min(2, "Name is required"),
  mobileNumber: z.string().min(10, "Valid phone required"),
  numberVerified: z.boolean(),
  source: z.nativeEnum(LeadSource),
  campaignId: z.string().optional(),
  spaceType: z.nativeEnum(SpaceType),
  seatRangeMin: z.coerce.number().min(1),
  seatRangeMax: z.coerce.number().min(1),
  urgency: z.nativeEnum(Urgency),
  location: z.string().min(2, "Location is required"),
  natureOfBusiness: z.string().min(1, "Nature of Business is required"),
  objectionType: z.enum(objectionTypeValues).optional(),
  objectionReason: z.string().optional(),
  expectedLocation: z.string().optional(),
  expectedPrice: z.string().optional(),
  spaceId: z.coerce.number().min(1, "Please select a space"),
  stage: z.nativeEnum(LeadStage),
  callStatus: z.nativeEnum(CallStatus),
  leadStatus: z.string().optional(),
  followUpCount: z.coerce.number().min(0),
  nextFollowUpDate: z.string().optional(),
  followUp1Date: z.string().optional(),
  followUp1Slot: z.string().optional(),
  followUp2Date: z.string().optional(),
  followUp2Slot: z.string().optional(),
  followUp3Date: z.string().optional(),
  followUp3Slot: z.string().optional(),
  callbackScheduledDate: z.string().optional(),
  callbackScheduledSlot: z.string().optional(),
  tourScheduledDate: z.string().optional(),
  tourScheduledSlot: z.string().optional(),
  tour1Date: z.string().optional(),
  tour1Slot: z.string().optional(),
  tour2Date: z.string().optional(),
  tour2Slot: z.string().optional(),
  tour3Date: z.string().optional(),
  tour3Slot: z.string().optional(),
}).superRefine((v, ctx) => {
  if (v.objectionType === "other" && !v.objectionReason?.trim()) {
    ctx.addIssue({ code: "custom", path: ["objectionReason"], message: "Please add the reason" });
  }
  if (v.objectionType === "location" && !v.expectedLocation?.trim()) {
    ctx.addIssue({ code: "custom", path: ["expectedLocation"], message: "Please add the expected location" });
  }
  if (v.objectionType === "price" && !v.expectedPrice?.trim()) {
    ctx.addIssue({ code: "custom", path: ["expectedPrice"], message: "Please add the customer's ideal price" });
  }
});

type LeadFormValues = z.infer<typeof formSchema>;

const EMPTY_FORM_VALUES: LeadFormValues = {
  customerName: "",
  mobileNumber: "",
  numberVerified: false,
  source: "walk_in" as LeadSource,
  campaignId: "",
  spaceType: "private_cabin" as SpaceType,
  seatRangeMin: 1,
  seatRangeMax: 1,
  urgency: "medium" as Urgency,
  location: "",
  natureOfBusiness: "",
  objectionType: "",
  objectionReason: "",
  expectedLocation: "",
  expectedPrice: "",
  spaceId: 0,
  stage: "not_contacted" as LeadStage,
  callStatus: "not_started" as CallStatus,
  leadStatus: "new",
  followUpCount: 0,
  nextFollowUpDate: "",
  followUp1Date: "",
  followUp1Slot: "",
  followUp2Date: "",
  followUp2Slot: "",
  followUp3Date: "",
  followUp3Slot: "",
  callbackScheduledDate: "",
  callbackScheduledSlot: "",
  tourScheduledDate: "",
  tourScheduledSlot: "",
  tour1Date: "",
  tour1Slot: "",
  tour2Date: "",
  tour2Slot: "",
  tour3Date: "",
  tour3Slot: "",
};

function mapLeadToFormValues(lead: Lead): LeadFormValues {
  return {
    customerName: lead.customerName,
    mobileNumber: lead.mobileNumber,
    numberVerified: lead.numberVerified,
    source: lead.source as LeadSource,
    campaignId: lead.campaignId ?? "",
    spaceType: lead.spaceType as SpaceType,
    seatRangeMin: lead.seatRangeMin,
    seatRangeMax: lead.seatRangeMax,
    urgency: lead.urgency as Urgency,
    location: lead.location,
    natureOfBusiness: lead.natureOfBusiness ?? "",
    objectionType: (lead.objectionType ?? "") as "" | "location" | "price" | "other",
    objectionReason: lead.objectionReason ?? "",
    expectedLocation: lead.expectedLocation ?? "",
    expectedPrice: lead.expectedPrice ?? "",
    spaceId: lead.spaceId,
    stage: lead.stage as LeadStage,
    callStatus: lead.callStatus as CallStatus,
    leadStatus: lead.leadStatus,
    followUpCount: lead.followUpCount,
    nextFollowUpDate: lead.nextFollowUpDate ? String(lead.nextFollowUpDate).slice(0, 10) : "",
    followUp1Date: lead.followUp1Date ? String(lead.followUp1Date).slice(0, 10) : "",
    followUp1Slot: lead.followUp1Slot ?? "",
    followUp2Date: lead.followUp2Date ? String(lead.followUp2Date).slice(0, 10) : "",
    followUp2Slot: lead.followUp2Slot ?? "",
    followUp3Date: lead.followUp3Date ? String(lead.followUp3Date).slice(0, 10) : "",
    followUp3Slot: lead.followUp3Slot ?? "",
    callbackScheduledDate: lead.callbackScheduledDate ? String(lead.callbackScheduledDate).slice(0, 10) : "",
    callbackScheduledSlot: lead.callbackScheduledSlot ?? "",
    tourScheduledDate: lead.tourScheduledDate ? String(lead.tourScheduledDate).slice(0, 10) : "",
    tourScheduledSlot: lead.tourScheduledSlot ?? "",
    tour1Date: lead.tour1Date ? String(lead.tour1Date).slice(0, 10) : "",
    tour1Slot: lead.tour1Slot ?? "",
    tour2Date: lead.tour2Date ? String(lead.tour2Date).slice(0, 10) : "",
    tour2Slot: lead.tour2Slot ?? "",
    tour3Date: lead.tour3Date ? String(lead.tour3Date).slice(0, 10) : "",
    tour3Slot: lead.tour3Slot ?? "",
  };
}

export default function NewLead() {
  const [matchEdit, editParams] = useRoute("/leads/:id/edit");
  const editId = matchEdit && editParams?.id ? parseInt(editParams.id) : 0;
  const isEdit = editId > 0;
  const { data: existingLead, isLoading: leadLoading } = useGetLead(editId, {
    query: { enabled: isEdit, queryKey: getGetLeadQueryKey(editId) },
  });
  const { data: spaces, isLoading: spacesLoading } = useListSpaces();

  // Wait until lead data is loaded before mounting the form, so useForm gets
  // the real defaultValues on its very first render. Otherwise the form mounts
  // with empty defaults and Radix Selects don't reliably re-bind through a
  // later form.reset() — leaving Selects visually empty even though state has
  // the right values.
  if (isEdit && (leadLoading || !existingLead)) {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <Skeleton className="h-9 w-48" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <LeadFormBody
      key={existingLead?.id ?? "new"}
      editId={editId}
      isEdit={isEdit}
      existingLead={existingLead}
      spaces={spaces}
      spacesLoading={spacesLoading}
    />
  );
}

function LeadFormBody({
  editId,
  isEdit,
  existingLead,
  spaces,
  spacesLoading,
}: {
  editId: number;
  isEdit: boolean;
  existingLead: Lead | undefined;
  spaces: Space[] | undefined;
  spacesLoading: boolean;
}) {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const createLead = useCreateLead();
  const updateLead = useUpdateLead();

  const form = useForm<LeadFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: existingLead ? mapLeadToFormValues(existingLead) : EMPTY_FORM_VALUES,
  });

  const callStatus = form.watch("callStatus");
  const leadStatusWatched = form.watch("leadStatus");
  useEffect(() => {
    if (callStatus === "follow_up_1") form.setValue("followUpCount", 1, { shouldDirty: true });
    else if (callStatus === "follow_up_2") form.setValue("followUpCount", 2, { shouldDirty: true });
    else if (callStatus === "follow_up_3") form.setValue("followUpCount", 3, { shouldDirty: true });
  }, [callStatus, form]);

  // On new lead, default spaceId to first available space (e.g. Hoblix Najafgarh).
  useEffect(() => {
    if (!isEdit && spaces && spaces.length > 0 && form.getValues("spaceId") === 0) {
      form.setValue("spaceId", spaces[0]!.id, { shouldDirty: false });
    }
  }, [isEdit, spaces, form]);

  // Radio state for Qualified → Schedule Tour | Follow Up. existingLead is
  // guaranteed loaded by the time we mount, so we can derive synchronously.
  const [nextAction, setNextAction] = useState<"tour" | "follow_up" | "">(() => {
    if (isEdit && existingLead) {
      if (existingLead.tourScheduledDate) return "tour";
      if (existingLead.callbackScheduledDate) return "follow_up";
    }
    return "";
  });

  const onSubmit = (raw: z.infer<typeof formSchema>) => {
    const data = Object.fromEntries(
      Object.entries(raw).map(([k, v]) => [k, v === "" ? undefined : v]),
    ) as Omit<typeof raw, "objectionType"> & { objectionType?: "location" | "price" | "other" };
    if (isEdit) {
      updateLead.mutate({ id: editId, data }, {
        onSuccess: (lead) => {
          toast({ title: "Success", description: "Lead updated successfully" });
          queryClient.invalidateQueries({ queryKey: getListLeadsQueryKey() });
          queryClient.invalidateQueries({ queryKey: getGetLeadQueryKey(editId) });
          queryClient.invalidateQueries({ queryKey: getGetLeadsSummaryQueryKey() });
          setLocation(`/leads/${lead.id}`);
        },
        onError: (error) => {
          toast({ variant: "destructive", title: "Error", description: error.message || "Failed to update lead" });
        },
      });
      return;
    }
    createLead.mutate({ data }, {
      onSuccess: (lead) => {
        toast({ title: "Success", description: "Lead created successfully" });
        queryClient.invalidateQueries({ queryKey: getListLeadsQueryKey() });
        queryClient.invalidateQueries({ queryKey: getGetLeadsSummaryQueryKey() });
        setLocation(`/leads/${lead.id}`);
      },
      onError: (error) => {
        toast({ variant: "destructive", title: "Error", description: error.message || "Failed to create lead" });
      }
    });
  };

  // Lead Status becomes terminal (locked) once saved as qualified or disqualified.
  const leadStatusLocked = !!(
    isEdit &&
    existingLead &&
    (existingLead.leadStatus === "qualified" || existingLead.leadStatus === "disqualified")
  );

  // Qualification is complete (and locked) only when an existing saved lead has
  // all 9 required fields populated. New leads are never locked.
  const qualificationLocked = !!(
    isEdit &&
    existingLead &&
    existingLead.mobileNumber &&
    existingLead.spaceId > 0 &&
    existingLead.spaceType &&
    existingLead.source &&
    existingLead.urgency &&
    existingLead.seatRangeMin >= 1 &&
    existingLead.seatRangeMax >= 1 &&
    (existingLead.location ?? "").trim().length > 0 &&
    (existingLead.natureOfBusiness ?? "").trim().length > 0
  );
  // On the fly: check current form values so subsequent sections can reveal
  // once the user has filled the required fields in a new lead.
  const v = form.watch();
  const qualificationComplete =
    qualificationLocked ||
    (
      !!v.mobileNumber && v.mobileNumber.length >= 10 &&
      !!v.spaceId && v.spaceId > 0 &&
      !!v.spaceType &&
      !!v.source &&
      !!v.urgency &&
      Number(v.seatRangeMin) >= 1 &&
      Number(v.seatRangeMax) >= 1 &&
      !!v.location && v.location.trim().length >= 2 &&
      !!v.natureOfBusiness && v.natureOfBusiness.trim().length > 0
    );

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight">{isEdit ? "Update Lead" : "New Lead"}</h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Lead Information</CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              {/* ============ SECTION 1: Qualification (locks when all filled + saved) ============ */}
              <fieldset disabled={qualificationLocked} className="space-y-6 disabled:opacity-80">
                {qualificationLocked && (
                  <div className="rounded-md border border-dashed bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                    Qualification details are locked. Contact an admin to edit.
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="customerName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Customer Name</FormLabel>
                        <FormControl><Input {...field} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="mobileNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Mobile Number</FormLabel>
                        <FormControl><Input {...field} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="spaceId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Target Space</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value ? String(field.value) : undefined} disabled={qualificationLocked || spacesLoading}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select a space" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {spaces?.map(space => (
                              <SelectItem key={space.id} value={String(space.id)}>{space.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="spaceType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Space Type</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value} disabled={qualificationLocked}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {Object.values(SpaceType).map(t => (
                              <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="source"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Lead Source</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value} disabled={qualificationLocked}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select source" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {Object.values(LeadSource).map(s => (
                              <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="urgency"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Urgency</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value} disabled={qualificationLocked}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select urgency" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {Object.values(Urgency).map(u => (
                              <SelectItem key={u} value={u}>{URGENCY_LABELS[u]}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="seatRangeMin"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Min Seats</FormLabel>
                        <FormControl><Input type="number" min={1} {...field} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="seatRangeMax"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Max Seats</FormLabel>
                        <FormControl><Input type="number" min={1} {...field} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="location"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Locality / Preferred Area</FormLabel>
                      <FormControl><Input placeholder="e.g. Downtown, Tech Park" {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <NatureOfBusinessField form={form} locked={qualificationLocked} />
              </fieldset>

              {/* ============ SECTION 2+ : revealed once qualification is complete ============ */}
              {qualificationComplete && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="stage"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Stage</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                            <SelectContent>
                              {Object.values(LeadStage).map(s => (
                                <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="callStatus"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Call Status</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                            <SelectContent>
                              {Object.values(CallStatus).filter((s) => s !== "follow_up").map(s => (
                                <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FollowUpFields form={form} callStatus={callStatus} />

                  {/* ============ SECTION 3: Lead Status ============
                      For new leads, only revealed once the call has connected.
                      For existing leads, always visible (locked if already set). */}
                  {(isEdit || callStatus === "connected") && (
                    <FormField
                      control={form.control}
                      name="leadStatus"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Lead Status</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value || "new"} disabled={leadStatusLocked}>
                            <FormControl><SelectTrigger className="w-[240px]"><SelectValue /></SelectTrigger></FormControl>
                            <SelectContent>
                              <SelectItem value="new">New</SelectItem>
                              <SelectItem value="qualified">Qualified</SelectItem>
                              <SelectItem value="disqualified">Disqualified</SelectItem>
                            </SelectContent>
                          </Select>
                          {leadStatusLocked ? (
                            <p className="text-xs text-muted-foreground">Locked once Qualified or Disqualified.</p>
                          ) : (
                            <p className="text-xs text-muted-foreground">Set after speaking with the customer. Cannot be changed once Qualified or Disqualified.</p>
                          )}
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  {/* ============ SECTION 4a: Objection — when Disqualified ============ */}
                  {callStatus === "connected" && leadStatusWatched === "disqualified" && (
                    <ObjectionPanel form={form} />
                  )}

                  {/* ============ SECTION 4b: Tour / Follow Up — when Qualified ============ */}
                  {callStatus === "connected" && leadStatusWatched === "qualified" && (
                    <div className="space-y-4 rounded-md border p-4">
                      <div>
                        <Label className="text-sm font-medium">Next Action</Label>
                        <RadioGroup
                          className="mt-2 flex gap-6"
                          value={nextAction}
                          onValueChange={(v) => setNextAction(v as "tour" | "follow_up")}
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="tour" id="action-tour" />
                            <Label htmlFor="action-tour" className="font-normal cursor-pointer">Schedule Tour</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="follow_up" id="action-followup" />
                            <Label htmlFor="action-followup" className="font-normal cursor-pointer">Follow Up</Label>
                          </div>
                        </RadioGroup>
                      </div>

                      {nextAction === "tour" && <TourFields form={form} />}

                      {nextAction === "follow_up" && (
                        <div className="grid grid-cols-2 gap-4">
                          <FormField
                            control={form.control}
                            name="callbackScheduledDate"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Follow-up Date</FormLabel>
                                <FormControl><Input type="date" min={todayIst()} {...field} /></FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name="callbackScheduledSlot"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Follow-up Time Slot</FormLabel>
                                <Select onValueChange={field.onChange} value={field.value || ""}>
                                  <FormControl><SelectTrigger><SelectValue placeholder="Select a slot" /></SelectTrigger></FormControl>
                                  <SelectContent>
                                    {TIME_SLOTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="campaignId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Campaign ID</FormLabel>
                          <FormControl><Input placeholder="e.g. META-WS-Q2" {...field} /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="followUpCount"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Follow-up Count</FormLabel>
                          <FormControl><Input type="number" min={0} readOnly tabIndex={-1} className="bg-muted/50 cursor-not-allowed" {...field} /></FormControl>
                          <p className="text-xs text-muted-foreground">Auto-updated from Call Status.</p>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="numberVerified"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel>Number Verified</FormLabel>
                        <FormControl>
                          <div className="flex items-center h-10">
                            <Switch checked={field.value} onCheckedChange={field.onChange} />
                            <span className="ml-2 text-sm text-muted-foreground">{field.value ? "Verified" : "Unverified"}</span>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </>
              )}

              {!qualificationComplete && (
                <p className="text-sm text-muted-foreground">
                  Fill all required fields above to continue (Mobile, Target Space, Space Type, Lead Source, Urgency, Min/Max Seats, Locality, Nature of Business).
                </p>
              )}

              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setLocation(isEdit ? `/leads/${editId}` : "/leads")}>Cancel</Button>
                <Button type="submit" disabled={createLead.isPending || updateLead.isPending}>
                  {isEdit
                    ? (updateLead.isPending ? "Saving..." : "Save Changes")
                    : (createLead.isPending ? "Creating..." : "Create Lead")}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}

function FollowUpFields({
  form,
  callStatus,
}: {
  form: ReturnType<typeof useForm<LeadFormValues>>;
  callStatus: string;
}) {
  return (
    <>
      {[1, 2, 3].map((n) => (
        <FollowUpRow key={n} form={form} callStatus={callStatus} index={n as 1 | 2 | 3} />
      ))}
    </>
  );
}

function FollowUpRow({
  form,
  callStatus,
  index,
}: {
  form: ReturnType<typeof useForm<LeadFormValues>>;
  callStatus: string;
  index: 1 | 2 | 3;
}) {
  const dateName = `followUp${index}Date` as const;
  const slotName = `followUp${index}Slot` as const;
  const date = form.watch(dateName);
  const slot = form.watch(slotName);
  const activeStatus = `follow_up_${index}`;
  const isActive = callStatus === activeStatus;
  const hasSaved = !!(date || slot);
  if (!isActive && !hasSaved) return null;
  const disabled = !isActive;
  return (
    <div className="grid grid-cols-2 gap-4">
      <FormField
        control={form.control}
        name={dateName}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Follow-up {index} Date</FormLabel>
            <FormControl>
              <Input type="date" min={todayIst()} disabled={disabled} {...field} />
            </FormControl>
            {disabled && <p className="text-xs text-muted-foreground">Saved when Call Status was Follow Up {index}.</p>}
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name={slotName}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Follow-up {index} Time Slot</FormLabel>
            <Select
              onValueChange={field.onChange}
              value={field.value || ""}
              disabled={disabled}
            >
              <FormControl>
                <SelectTrigger><SelectValue placeholder="Select a slot" /></SelectTrigger>
              </FormControl>
              <SelectContent>
                {TIME_SLOTS.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}

function TourFields({ form }: { form: ReturnType<typeof useForm<LeadFormValues>> }) {
  const t1 = !!(form.watch("tour1Date") || form.watch("tour1Slot"));
  const t2 = !!(form.watch("tour2Date") || form.watch("tour2Slot"));
  const t3 = !!(form.watch("tour3Date") || form.watch("tour3Slot"));
  // The next empty row (1, 2, or 3) is the one the user can currently edit.
  // When all three are filled, nothing is editable.
  const editableIndex = !t1 ? 1 : !t2 ? 2 : !t3 ? 3 : 0;
  return (
    <>
      {[1, 2, 3].map((i) => {
        const hasSaved = i === 1 ? t1 : i === 2 ? t2 : t3;
        const isEditable = i === editableIndex;
        if (!hasSaved && !isEditable) return null;
        return <TourRow key={i} form={form} index={i as 1 | 2 | 3} disabled={!isEditable} />;
      })}
    </>
  );
}

function TourRow({
  form,
  index,
  disabled,
}: {
  form: ReturnType<typeof useForm<LeadFormValues>>;
  index: 1 | 2 | 3;
  disabled: boolean;
}) {
  const dateName = `tour${index}Date` as const;
  const slotName = `tour${index}Slot` as const;
  return (
    <div className="grid grid-cols-2 gap-4">
      <FormField
        control={form.control}
        name={dateName}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Schedule {index} Date</FormLabel>
            <FormControl>
              <Input type="date" min={todayIst()} disabled={disabled} {...field} />
            </FormControl>
            {disabled && <p className="text-xs text-muted-foreground">Previously scheduled tour.</p>}
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name={slotName}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Schedule {index} Time Slot</FormLabel>
            <Select onValueChange={field.onChange} value={field.value || ""} disabled={disabled}>
              <FormControl><SelectTrigger><SelectValue placeholder="Select a slot" /></SelectTrigger></FormControl>
              <SelectContent>
                {TIME_SLOTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}

function NatureOfBusinessField({
  form,
  locked = false,
}: {
  form: ReturnType<typeof useForm<LeadFormValues>>;
  locked?: boolean;
}) {
  const value = form.watch("natureOfBusiness") ?? "";
  const isPredefined = (NATURE_OF_BUSINESS_OPTIONS as readonly string[]).includes(value);
  const [customMode, setCustomMode] = useState<boolean>(!!value && !isPredefined);

  useEffect(() => {
    if (value && !isPredefined) setCustomMode(true);
    else if (isPredefined) setCustomMode(false);
  }, [value, isPredefined]);

  return (
    <FormField
      control={form.control}
      name="natureOfBusiness"
      render={({ field }) => (
        <FormItem>
          <FormLabel>Nature of Business</FormLabel>
          <Select
            value={customMode ? "__other__" : (isPredefined ? value : "")}
            onValueChange={(v) => {
              if (v === "__other__") {
                setCustomMode(true);
                field.onChange("");
              } else {
                setCustomMode(false);
                field.onChange(v);
              }
            }}
            disabled={locked}
          >
            <FormControl>
              <SelectTrigger><SelectValue placeholder="Select nature of business" /></SelectTrigger>
            </FormControl>
            <SelectContent>
              {NATURE_OF_BUSINESS_OPTIONS.map((o) => (
                <SelectItem key={o} value={o}>{o}</SelectItem>
              ))}
              <SelectItem value="__other__">Other (add custom)</SelectItem>
            </SelectContent>
          </Select>
          {customMode && (
            <FormControl>
              <Input autoFocus placeholder="Enter nature of business" disabled={locked} {...field} />
            </FormControl>
          )}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

function ObjectionPanel({ form }: { form: ReturnType<typeof useForm<LeadFormValues>> }) {
  const objType = form.watch("objectionType");
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Objection (if not qualified)</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <FormField
          control={form.control}
          name="objectionType"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Objection Type</FormLabel>
              <Select
                onValueChange={(v) => field.onChange(v === "none" ? "" : v)}
                value={field.value ? field.value : "none"}
              >
                <FormControl><SelectTrigger className="w-[260px]"><SelectValue placeholder="None" /></SelectTrigger></FormControl>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="location">Location</SelectItem>
                  <SelectItem value="price">Price</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        {objType === "location" && (
          <FormField
            control={form.control}
            name="expectedLocation"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Expected Location</FormLabel>
                <FormControl><Input placeholder="Where is the customer expecting a space?" {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        {objType === "price" && (
          <FormField
            control={form.control}
            name="expectedPrice"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Customer's Ideal Price</FormLabel>
                <FormControl><Input placeholder="e.g. ₹6000 / seat / month, or ₹40k–50k total" {...field} /></FormControl>
                <p className="text-xs text-muted-foreground">What price (or range) is the customer expecting?</p>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        {objType === "other" && (
          <FormField
            control={form.control}
            name="objectionReason"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Reason</FormLabel>
                <FormControl><Textarea rows={3} placeholder="Describe the objection" {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
      </CardContent>
    </Card>
  );
}
